import { useMemo } from 'react';
import memoizeOne from 'memoize-one';
import { measureFunc } from '@atlassian/jira-common-performance/src/marks.tsx';
import { useFieldsValuesActions } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import type { aggBacklogDataQuery$data } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type { PrefetchedDataGql } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import type {
	Issue,
	JiraPlanDataRestResponse,
} from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import type { IssueParent } from '../../../model/issue-parent/types';
import type { CardCreateGql } from '../../../rest/card-create-multiple-gql/types';
import type { EpicIssueTypeState } from '../../../state/reducers/config/epic/types';
import { extractedFixedFields } from './constants';
import type { ExtractedCustomFields, FieldConfigs, StoreFieldsActions } from './types';
import {
	mergeFieldsFromCardCreateData,
	mergeFieldsFromFetchIssuesData,
	mergeFieldsFromPrefetchedAggData,
	mergeFieldsFromPrefetchedData,
	mergeFieldsFromPrefetchedJiraPlanData,
} from './utils';

export const getExtractedFields = memoizeOne(
	(extractedCustomFields: ExtractedCustomFields): string[] =>
		Object.values(extractedCustomFields).reduce(
			(extractedFields, extractedCustomFieldId) => {
				const extractedCustomField: string = extractedCustomFieldId || '';
				if (extractedCustomField) {
					extractedFields.push(extractedCustomField);
				}
				return extractedFields;
			},
			[...extractedFixedFields],
		),
);

export const useStoreFields = ({
	issueParents,
}: { issueParents?: IssueParent[] } = {}): StoreFieldsActions => {
	const [, fieldsValueActions] = useFieldsValuesActions();

	const storeFieldsActions: StoreFieldsActions = useMemo(() => {
		const storeFieldsFromCardCreateData = (
			cardCreateData: CardCreateGql,
			fieldConfigs: FieldConfigs,
		) => {
			mergeFieldsFromCardCreateData(cardCreateData, fieldsValueActions, fieldConfigs, issueParents);
		};
		const storeFieldsFromFetchIssuesData = (
			issues: Issue[] | null,
			epicIssueType: EpicIssueTypeState | null,
		) => {
			measureFunc('storeFieldsFromFetchIssuesData', () => {
				mergeFieldsFromFetchIssuesData(issues, fieldsValueActions, epicIssueType);
			});
		};

		const storeFieldsFromPrefetchedData = (
			prefetchedData: PrefetchedDataGql,
			fieldConfigs: FieldConfigs,
		) =>
			measureFunc('storeFieldsFromPrefetchedData', () => {
				mergeFieldsFromPrefetchedData(prefetchedData, fieldsValueActions, fieldConfigs);
			});

		const storeFieldsFromPrefetchedAggData = (
			prefetchedAggData: aggBacklogDataQuery$data,
			fieldConfigs: FieldConfigs,
		) =>
			measureFunc('storeFieldsFromPrefetchedAggData', () => {
				mergeFieldsFromPrefetchedAggData(prefetchedAggData, fieldsValueActions, fieldConfigs);
			});

		const storeFieldsFromPrefetchedPlanData = (
			prefetchedJiraPlanRestData: JiraPlanDataRestResponse,
			epicIssueType: EpicIssueTypeState | null,
		) =>
			measureFunc('storeFieldsFromPrefetchedJiraPlanRestData', () => {
				mergeFieldsFromPrefetchedJiraPlanData(
					prefetchedJiraPlanRestData,
					fieldsValueActions,
					epicIssueType,
				);
			});

		return {
			storeFieldsFromCardCreateData,
			storeFieldsFromFetchIssuesData,
			storeFieldsFromPrefetchedData,
			storeFieldsFromPrefetchedAggData,
			storeFieldsFromPrefetchedPlanData,
		};
	}, [fieldsValueActions, issueParents]);

	return storeFieldsActions;
};
